import { useDisplayOptionContext } from '@/hooks/useDisplayOptionContext';
import { useUnitItemContext } from '@/hooks/useUnitItemContext';
import { useUnitJourneyContext } from '@/hooks/useUnitJourneyContext';
import React from 'react';
import { Button } from '@/components/ui/button';
import { UnitRentButton } from '@/components/units/action/unitRentButton';
import { UnitReserveButton } from '@/components/units/action/unitReserveButton';
import { UnitQuoteButton } from '@/components/units/action/unitQuoteButton';
import { useFacilitiesContext } from '@/hooks/useFacilitiesContext';

export const TriggerButtons = React.forwardRef<HTMLDivElement>((_props, ref) => {
    const { unitGroup } = useUnitItemContext();
    const displayOptions = useDisplayOptionContext();
    const journeyContext = useUnitJourneyContext();
    const facilitiesContext = useFacilitiesContext();

    const { buttonLayout, disableRental, disableReservation, disableSoftReservation, unavailableUnits } = displayOptions;
    const { facilities = [] } = facilitiesContext;
    const facilityInfo = facilities.find((facility) => facility.id === unitGroup.representative_unit.facility_id);
    const hasMultipleSubGroups = Array.isArray(unitGroup.sub_groups) && unitGroup.sub_groups.length > 1;
    const isAvailable = unitGroup.available > 0;

    const reservationWindowAllowed = React.useMemo(() => {
        return unitGroup.reservation_allowed;
    }, [unitGroup]);

    const handleSelectEvent = (step: string) => {
        journeyContext.setIsOpen(!journeyContext.isOpen);
        journeyContext.setUnitJourneyStep(step);
    }

    const renderSelectButton = () => (
        <Button
            variant="default"
            data-testid="selectButton"
            onClick={() => handleSelectEvent(hasMultipleSubGroups ? "vbp" : "rent-reserve-quote")}
        >
            { displayOptions.language?.action?.selectButton }
        </Button>
    );

    const renderActionButtons = () => (
        <div ref={ref}>
            {!disableRental && (
                <UnitRentButton
                    unitId={unitGroup.representative_unit.id}
                    {...(hasMultipleSubGroups && { onClick: () => { handleSelectEvent("vbp") } })}
                />
            )}
            {!disableReservation && reservationWindowAllowed && (
                <UnitReserveButton unitId={unitGroup.representative_unit.id} />
            )}
        </div>
    );

    const renderQuoteButton = () => (
        <div ref={ref}>
            <UnitQuoteButton unitId={unitGroup.representative_unit.id} />
        </div>
    );

    const renderCallForAvailability = () => (
            <div ref={ref} className='facility-call-availability'>
                Call for Availability <br />
                <span>{facilityInfo?.phone ?? ""}</span>
            </div>
    );

    // We need to check if vbp is enabled and the unit has multiple sub-groups and the button layout is separate
    if (buttonLayout === "separate" && hasMultipleSubGroups && facilityInfo?.settings?.hide_reservation_button_on_vbp_modal) {
        return renderActionButtons();
    }

    // Combined condition for showing select button
    if ((buttonLayout === "combined" || hasMultipleSubGroups) && isAvailable && !(disableRental && disableReservation && disableSoftReservation)) {
        return renderSelectButton();
    }

    // Unavailable units with quote option
    if ((disableRental || disableReservation ||
        (!isAvailable && unavailableUnits)) && !disableSoftReservation) {
        return renderQuoteButton();
    }

    // Available units with individual buttons
    if (isAvailable && !(disableRental && disableReservation && disableSoftReservation)) {
        return renderActionButtons();
    }

    // Default fallback
    return renderCallForAvailability();
});

TriggerButtons.displayName = 'TriggerButtons';